import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

interface CatalogFilterInfoBannerProps {
  /** Layout overrides (e.g. margin) — each drawer has different header padding. */
  className?: string;
  /** Automation hook (defaults `catalog-filter-info-banner` / `catalog-filter-info-tooltip`). */
  testId?: string;
}

/**
 * Info banner shown at the top of a catalog-derived filter drawer, explaining
 * that filter options (and their absence) reflect the user's imported catalog.
 *
 * <p>Drawers are wide enough to carry the copy inline. In the narrow rail use
 * {@link CatalogFilterInfoTooltip} instead — there the banner wraps to seven
 * lines and pushes the facets below the fold.
 */
export function CatalogFilterInfoBanner({
  className,
  testId = 'catalog-filter-info-banner',
}: CatalogFilterInfoBannerProps) {
  const { t } = useTranslation('common');

  return (
    <div
      className={cn('flex items-start gap-2.5 rounded-xl bg-blue-50 px-4 py-3', className)}
      data-testid={testId}
    >
      <Info className="text-primary mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <p className="text-sm leading-relaxed">{t('catalogFilterInfoBanner')}</p>
    </div>
  );
}

/**
 * The same explanation as {@link CatalogFilterInfoBanner}, collapsed to an info
 * icon that reveals the copy on hover or keyboard focus. For the 13.5rem filter
 * rail, where the banner costs more vertical space than the facets it explains.
 *
 * The trigger is a real `button` so the tooltip is reachable by keyboard, and
 * the copy is also attached as `aria-label` so screen readers get it without
 * depending on tooltip semantics.
 */
export function CatalogFilterInfoTooltip({
  className,
  testId = 'catalog-filter-info-tooltip',
}: CatalogFilterInfoBannerProps) {
  const { t } = useTranslation('common');
  const copy = t('catalogFilterInfoBanner');

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={copy}
            data-testid={testId}
            className={cn(
              'text-muted-foreground hover:text-primary cursor-help transition-colors',
              className,
            )}
          >
            <Info className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="right"
          className="max-w-64 text-xs leading-relaxed"
          data-testid={`${testId}-content`}
        >
          {copy}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
